import React from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import classNames from 'classnames';
import { get, memoize } from 'lodash';
import Button from '@atlaskit/button';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import AddIcon from '@atlaskit/icon/glyph/add';
import ToolTip from '@atlaskit/tooltip';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { EntitlementIcon, OpenSidebarButton } from '@css-xp/base';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths } from '@css-xp/ppc-constants';
import { NoData } from '@css-xp/ppc-common';
import { CATALOGUE_PAGE_SIDEBARS, TABLE_HEADER } from '../constants/table';
import './products-table-items.scss';

interface Props {
  products: PartnerProductCatalogueItem[];
  quoteItems: PartnerProductCatalogueItem[];
  setCatalogueItem: (item: PartnerProductCatalogueItem) => void;
  setIsPartnerCatalogueInformationLoading: (flag: boolean) => void;
  txaId?: string;
  setEstimatesToBeRefreshed: (flag: boolean) => void;
  isRowClickable?: boolean;
  existingCloudSiteProductIds?: string[];
  catalogExcludedProducts?: string[];
  isEnterpriseOfferingEnabled?: boolean;
  isJSMRepackagingEnabled?: boolean;
  isCompassPremiumEnabled?: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
  isAppificationEnabled?: boolean;
  version?: number;
}
const createHeadV2Resolver = (version?: number, isAppificationEnabled?: boolean) =>
  `${version}-${isAppificationEnabled}`;

export const createHeadV2 = memoize((version?: number, isAppificationEnabled?: boolean) => {
  if (version === 2) {
    return [
      {
        name: isAppificationEnabled ? TABLE_HEADER.APPS : TABLE_HEADER.PRODUCT,
        content: TABLE_HEADER.PRODUCT,
        customClassNames: 'col-70',
        ariaLabel: 'List of items',
      },
      { name: TABLE_HEADER.PARTNER_DISCOUNT, content: TABLE_HEADER.PARTNER_DISCOUNT, customClassNames: 'col-20' },
      { name: TABLE_HEADER.ACTION, content: 'Details', customClassNames: 'col-10' },
    ];
  }
  return [
    { name: TABLE_HEADER.PRODUCT, content: TABLE_HEADER.PRODUCT, customClassNames: 'col-50' },
    { name: TABLE_HEADER.PARTNER_DISCOUNT, content: TABLE_HEADER.PARTNER_DISCOUNT, customClassNames: 'col-15' },
    { name: TABLE_HEADER.LAST_MODIFIED, content: TABLE_HEADER.LAST_MODIFIED, customClassNames: 'col-15' },
    { name: '', content: 'Details', customClassNames: 'col-10' },
    { name: '', content: 'Select', customClassNames: 'col-10' },
  ];
}, createHeadV2Resolver);

export type ProductCatalogueItem = PartnerProductCatalogueItem & {
  hasOptedInForPartnerPromotion?: boolean;
  vendorName?: string | null;
};

export const createRowsV2 = ({
  products,
  quoteItems,
  setCatalogueItem,
  setIsPartnerCatalogueInformationLoading,
  txaId,
  setEstimatesToBeRefreshed,
  isRowClickable,
  existingCloudSiteProductIds,
  catalogExcludedProducts,
  isEnterpriseOfferingEnabled,
  isJSMRepackagingEnabled,
  isCompassPremiumEnabled,
  isJPDAsJiraFamilyEnabled,
  version,
  isAppificationEnabled,
}: Props) => {
  const isVersion2 = version === 2;
  const updatedProducts = (products ?? []).filter((product) => !catalogExcludedProducts?.includes(product?.id));
  return updatedProducts.map((product: ProductCatalogueItem, index: number) => {
    const name = product?.name;
    const priceUpdatedContent = get(product, 'updatedDate');
    const isChecked = quoteItems.some((item) => item?.id === product?.id);
    const vendorName = product?.vendorName;

    // This condition is to block existing cloudsite subscription products. if product is already there in cloudsite subscriptions then we should not allow again to add product
    const isProductNotInCloudSiteSubscription = !existingCloudSiteProductIds?.includes(product.id);

    // few products are not app and also not enabled for order
    const isEnabledForOrder =
      (product?.isEnabledForOrder || product?.isApp) &&
      !name.toLowerCase().includes('statuspage') &&
      isRowClickable &&
      isProductNotInCloudSiteSubscription;

    const handleAddProduct = () => {
      if (!isChecked) {
        setIsPartnerCatalogueInformationLoading(true);
      }
      setCatalogueItem({ ...product });
      setEstimatesToBeRefreshed(true);
    };

    const rowData = createHeadV2(version, isAppificationEnabled).map((column) => {
      switch (column.content) {
        case TABLE_HEADER.PRODUCT:
          return (
            <td
              className="catalog-table-product"
              onClick={() => !isVersion2 && isEnabledForOrder && handleAddProduct()}
            >
              <EntitlementIcon
                productId={product?.id ?? ''}
                className="avatar medium"
                isCloud={true}
                logoId={product?.logoId}
                aria-label={name}
              />
              <div className="catalog-table-product-detail">
                <div className="catalog-table-product-detail-name">{name}</div>
                <div className="catalog-table-product-detail-vendorName" aria-label={`Vendor: ${vendorName}`}>
                  {vendorName}
                </div>
              </div>
            </td>
          );
        case TABLE_HEADER.PARTNER_DISCOUNT:
          return (
            <td onClick={() => !isVersion2 && isEnabledForOrder && handleAddProduct()}>
              {product?.hasOptedInForPartnerPromotion ? (
                <div className="partner-promotion-check" data-testid="partner-promotion-check">
                  {/* FIX ME: token was not taking correct color value */}
                  {isVersion2 ? (
                    <CheckCircleOutlineIcon size="medium" label="" aria-label="Partner discount available" />
                  ) : (
                    <CheckCircleIcon
                      size="medium"
                      label=""
                      primaryColor={'#626F86'}
                      aria-label="Partner discount available"
                    />
                  )}
                </div>
              ) : isVersion2 ? (
                <span>N/A</span>
              ) : (
                <NoData className="flex align-center justify-center" />
              )}
            </td>
          );
        case TABLE_HEADER.LAST_MODIFIED:
          return (
            <td onClick={() => isEnabledForOrder && handleAddProduct()}>
              {priceUpdatedContent ? <>{format(parseISO(priceUpdatedContent), 'PP')}</> : <NoData />}
            </td>
          );
        case 'Details':
          return (
            <td className={classNames({ 'details-btn': isVersion2 })}>
              {isVersion2 ? (
                <Link
                  to={`${getRouteUrl(routePaths.catalogDetails, {
                    txaId: txaId as string,
                    productId: product?.id,
                  })}`}
                >
                  <Button appearance="subtle" spacing="compact">
                    Details
                  </Button>
                </Link>
              ) : (
                <OpenSidebarButton
                  sidebar={CATALOGUE_PAGE_SIDEBARS.PRODUCT_PRICING_PLAN}
                  testId={'product-details-btn'}
                  sidebarOptions={{
                    sidebarWidth: '60%',
                    isOverlay: true,
                    className: 'sidebar-extra test',
                  }}
                  payload={{
                    txaId,
                    product,
                    hasOptedInForPartnerPromotion: product?.hasOptedInForPartnerPromotion,
                    isEnterpriseOfferingEnabled,
                    isJSMRepackagingEnabled,
                    isCompassPremiumEnabled,
                    isJPDAsJiraFamilyEnabled,
                  }}
                >
                  Details
                </OpenSidebarButton>
              )}
            </td>
          );
        case 'Select':
          return (
            <td>
              {isEnabledForOrder ? (
                <Button
                  testId={'product-add-remove-btn'}
                  className={'add-remove-cta-btn'}
                  onClick={handleAddProduct}
                  iconBefore={
                    isChecked ? (
                      <CrossIcon label="" size="small" testId="remove-product-icon" />
                    ) : (
                      <AddIcon label="" size="small" testId="add-product-icon" />
                    )
                  }
                />
              ) : (
                <ToolTip
                  position={'top'}
                  content={'You can’t place an order for this item'}
                  testId="ppc-checkout-ordering-disabled-info"
                >
                  <Button
                    testId={'product-add-remove-btn'}
                    className={'add-remove-cta-btn-disable'}
                    isDisabled={true}
                    iconBefore={<CrossIcon label="" size="small" />}
                  />
                </ToolTip>
              )}
            </td>
          );
      }
    });
    return (
      <tr key={`row-${index}-${name}`} className={classNames({ 'clickable-row': isEnabledForOrder })}>
        {rowData}
      </tr>
    );
  });
};
